/**
 * Component: IssueEventReceiptPrompt
 * After recording an event payment, offer the official receipt (receipts
 * module). The issued receipt is linked to the ledger entry (source_type
 * 'event_payment' + event_payments.receipt_id).
 */

import { formatBs } from '@/lib/money';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { IssueReceiptForm } from '@/features/receipts/components/IssueReceiptForm';
import { useToast } from '@/hooks/use-toast';
import { linkPaymentReceipt, type EventPaymentMethod } from '@/Services/eventPayments.service';
import type { ParticipantWithDetails } from '@/Services/eventParticipants.service';

interface Props {
  eventName: string;
  paymentId: string;
  participant: ParticipantWithDetails;
  amount: number;
  method: EventPaymentMethod;
  /** Open the receipt form directly (e.g. from the payments list) */
  skipPrompt?: boolean;
  onClose: () => void;
}

export function IssueEventReceiptPrompt({ eventName, paymentId, participant, amount, method, skipPrompt, onClose }: Props) {
  const [showForm, setShowForm] = useState(!!skipPrompt);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const payerName = participant.minister?.full_name ?? participant.guest?.full_name ?? '';

  if (showForm) {
    return (
      <IssueReceiptForm
        defaultCategoryCode="ING"
        initialPayerName={payerName}
        initialPayerCi={participant.minister?.dni ?? participant.guest?.dni ?? undefined}
        ministerId={participant.minister_id ?? undefined}
        sourceType="event_payment"
        sourceId={paymentId}
        initialLines={[
          { concept_id: '', description: `Inscripción — ${eventName}`, quantity: '1', unit_price: amount.toFixed(2) },
        ]}
        initialPayments={[
          { method, amount: amount.toFixed(2), bank_account_id: '', check_number: '', voucher_photo_url: '' },
        ]}
        onClose={onClose}
        onIssued={async (result) => {
          try {
            await linkPaymentReceipt(paymentId, result.id);
            queryClient.invalidateQueries({ queryKey: ['event_payments'] });
          } catch (err) {
            toast({ title: err instanceof Error ? err.message : 'No se pudo vincular el recibo', variant: 'destructive' });
          }
          onClose();
        }}
      />
    );
  }

  return (
    <AlertDialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>¿Emitir recibo?</AlertDialogTitle>
          <AlertDialogDescription>
            Pago de {formatBs(amount)} de {payerName} registrado. Puede emitir el recibo oficial ahora o más tarde
            desde la pestaña Pagos.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Más tarde</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              setShowForm(true);
            }}
          >
            Emitir recibo
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
